import { Theme } from 'widget/entities/themes';

import type { LayoutVersion, MessageSource, Placement } from './common';

/**
 * События от inject скрипта к iframe
 */
export enum InjectEvents {
  /** Изменение layout версии (mobile/desktop) */
  LAYOUT_CHANGED = 'injectLayoutChanged',
  /** Изменение размещения виджета (left/right) */
  PLACEMENT_CHANGED = 'injectPlacementChanged',
  /** Подтверждение открытия виджета (стили применены) */
  OPEN_CONFIRMED = 'injectOpenConfirmed',
  /** Ответ на запрос токена авторизации */
  AUTH_TOKEN_RESPONSE = 'injectAuthTokenResponse'
}

/**
 * События от iframe к inject скрипту
 */
export enum WidgetEvents {
  /** Успешная верификация виджета */
  VERIFICATION_SUCCESS = 'widgetVerificationCompleteSuccess',
  /** Ошибка верификации виджета */
  VERIFICATION_ERROR = 'widgetVerificationCompleteError',
  /** Запрос на открытие виджета */
  OPEN_REQUEST = 'widgetOpenRequest',
  /** Виджет закрыт */
  CLOSED = 'widgetClosed',
  /** Скрыть виджет до перезагрузки страницы */
  HIDE_UNTIL_RELOAD_REQUEST = 'widgetHideUntilReloadRequest',
  /** Сохранение нового JWT токена в localStorage */
  SAVE_TOKEN = 'widgetSaveToken',
  /** Запрос токена авторизации для API запросов */
  REQUEST_AUTH_TOKEN = 'widgetRequestAuthToken',
  /** MediaViewer открыт (iframe нужно растянуть до 100% viewport) */
  MEDIA_VIEWER_OPENED = 'widgetMediaViewerOpened',
  /** MediaViewer закрыт (iframe вернуть к обычному размеру) */
  MEDIA_VIEWER_CLOSED = 'widgetMediaViewerClosed',
  /** Resolved тема виджета изменилась (для синхронизации color-scheme на iframe) */
  THEME_RESOLVED = 'widgetThemeResolved'
}

/**
 * Payload для события изменения layout
 */
export interface LayoutChangedPayload {
  layoutVersion: LayoutVersion;
}

/**
 * Payload для события изменения placement
 */
export interface PlacementChangedPayload {
  placement: Placement;
}

/**
 * Payload для передачи JWT токена
 */
export interface TokenPayload {
  token: string | null;
}

/**
 * Payload для передачи resolved color-scheme виджета
 */
export interface ThemeResolvedPayload {
  colorScheme: Theme;
}

/**
 * Типизированные сообщения от inject скрипта к iframe
 */
export type InjectToWidgetMessage =
  | {
      source: MessageSource.INJECT;
      event: InjectEvents.LAYOUT_CHANGED;
      payload: LayoutChangedPayload;
    }
  | {
      source: MessageSource.INJECT;
      event: InjectEvents.PLACEMENT_CHANGED;
      payload: PlacementChangedPayload;
    }
  | {
      source: MessageSource.INJECT;
      event: InjectEvents.OPEN_CONFIRMED;
      payload?: never;
    }
  | {
      source: MessageSource.INJECT;
      event: InjectEvents.AUTH_TOKEN_RESPONSE;
      payload: TokenPayload;
    };

export type WidgetToInjectMessage =
  | {
      source: MessageSource.WIDGET;
      event: Exclude<
        WidgetEvents,
        WidgetEvents.SAVE_TOKEN | WidgetEvents.THEME_RESOLVED
      >;
      payload?: never;
    }
  | {
      source: MessageSource.WIDGET;
      event: WidgetEvents.SAVE_TOKEN;
      payload: TokenPayload;
    }
  | {
      source: MessageSource.WIDGET;
      event: WidgetEvents.THEME_RESOLVED;
      payload: ThemeResolvedPayload;
    };

/**
 * Все возможные сообщения между inject и iframe
 */
export type PostMessage = InjectToWidgetMessage | WidgetToInjectMessage;
